import type { Role } from '../api/generated/model';

export interface NavItem {
  readonly to: string;
  readonly labelKey: string;
}

const HOME: NavItem = { to: '/', labelKey: 'nav.home' };
// A caixa de mensagens vale para todo papel: a conversa com o Super Admin
// existe para todo mundo, sempre, e por isso o item nunca leva a tela vazia.
const MESSAGES: NavItem = { to: '/messages', labelKey: 'nav.messages' };
// Anuncio chega a todo papel; o Super Admin ainda ganha a redacao na mesma tela.
const ANNOUNCEMENTS: NavItem = { to: '/announcements', labelKey: 'nav.announcements' };
// Preferencia de lembrete e de cada pessoa, e nao um ajuste de administrador.
const REMINDERS: NavItem = { to: '/settings/reminders', labelKey: 'nav.reminders' };
const SCHEDULE: NavItem = { to: '/schedule', labelKey: 'nav.schedule' };
// Proposta de mudanca chega a professor e aluno; a fila de colisao e so do
// Super Admin, e a propria tela esconde a secao para os outros papeis. Um item
// so, porque as duas filas sao o mesmo assunto visto dos dois lados.
const SCHEDULE_CHANGES: NavItem = { to: '/schedule/changes', labelKey: 'nav.scheduleChanges' };
// A grade recorrente e cadastro, e cadastro e exclusivo de Super Admin.
const SERIES: NavItem = { to: '/series', labelKey: 'nav.series' };
const RECORDINGS: NavItem = { to: '/recordings', labelKey: 'nav.recordings' };
const MAKEUP: NavItem = { to: '/makeup', labelKey: 'nav.makeup' };
const ATTENDANCE: NavItem = { to: '/attendance', labelKey: 'nav.attendance' };
const STUDENTS: NavItem = { to: '/students', labelKey: 'nav.students' };
const TEACHERS: NavItem = { to: '/teachers', labelKey: 'nav.teachers' };
const PLANS: NavItem = { to: '/plans', labelKey: 'nav.plans' };
const ENROLLMENTS: NavItem = { to: '/enrollments', labelKey: 'nav.enrollments' };
const MY_ENROLLMENT: NavItem = { to: '/my-enrollment', labelKey: 'nav.myEnrollment' };
// A situacao financeira e do aluno; o financeiro administrativo e outra tela,
// porque quem concilia nao esta olhando a propria fatura.
const BILLING: NavItem = { to: '/billing', labelKey: 'nav.billing' };
const FINANCE: NavItem = { to: '/finance', labelKey: 'nav.finance' };
// O catalogo de precos e a politica de inadimplencia tem item proprio porque e
// por ele que a pendencia de placeholder da §15.2 se resolve: escondido dentro
// do financeiro administrativo, o caminho para desbloquear o boot de producao
// so seria achado por quem ja sabe que ele existe.
const PRICING: NavItem = { to: '/finance/pricing', labelKey: 'nav.pricing' };
// Os ganhos sao do professor e so dele: `getMyEarnings` e sobre o solicitante,
// e nao ha versao administrativa desta tela — o Super Admin ve a folha pelo lote.
const EARNINGS: NavItem = { to: '/earnings', labelKey: 'nav.earnings' };
// O fechamento da folha e exclusivo de Super Admin no contrato; para os outros
// papeis o item nem aparece, porque menu que leva a tela recusada e ruido.
const PAYROLL: NavItem = { to: '/payroll', labelKey: 'nav.payroll' };
// `getExecutiveDashboard` e exclusiva de Super Admin, e o painel e agregado:
// nao lista pessoa nenhuma.
const EXECUTIVE: NavItem = { to: '/dashboards/executive', labelKey: 'nav.executive' };
// A biblioteca de materiais e `ANY_ROLE` na matriz de rotas, e o menu passa a
// dizer o mesmo. Ela existia, tinha rota e consumia operacao normativa, e nao
// havia UM link para ela em papel nenhum: so digitando /materials na barra de
// endereco. O proprio recorte do que cada pessoa ve e feito pelo servico, e a
// tela ja esconde a acao de apagar para quem nao administra.
const MATERIALS: NavItem = { to: '/materials', labelKey: 'nav.materials' };
// `listMyPerformance` e do aluno e so dele — a visao de professor e de Super
// Admin e `/students/:studentId/performance`, que tem parametro e por isso nunca
// aparece em menu nenhum.
const MY_PERFORMANCE: NavItem = { to: '/academic/performance', labelKey: 'nav.myPerformance' };
// Tipos e escalas de avaliacao. Item proprio, e nao secao escondida dentro de
// outra tela, pelo mesmo motivo de PRICING: e por aqui que se ratifica a
// configuracao que o registro de aula exige, e caminho que so quem ja sabe que
// existe encontra e caminho que ninguem percorre.
const ASSESSMENT_SETTINGS: NavItem = {
  to: '/academic/settings',
  labelKey: 'nav.assessmentSettings',
};

/**
 * O menu de cada papel — os quatro escritos, um a um.
 *
 * Era um `switch` cujo `default` entregava ao suporte o menu dele por OMISSAO, e
 * nao por decisao. Enquanto o enum teve quatro papeis nada aparecia, mas e a
 * mesma forma de defeito que `routes.tsx` acabou de abandonar em `ROLE_HOME`, e
 * o docstring de la ja escreveu a regra desta casa: "com o mapa total, o papel
 * que o contrato acrescentar **nao compila** ate alguem decidir qual painel ele
 * ve; com mais um `case`, o proximo papel cairia calado no `default` exatamente
 * como este caiu".
 *
 * Aqui a consequencia e maior que na home, e por isso o conserto vem junto: este
 * mapa decide o que cada pessoa consegue ALCANCAR na plataforma, e `SupportHome`
 * (`routes.tsx`) deriva o painel do suporte de `navItemsFor('support')` — a
 * mesma omissao escolheria calada o menu E a home do papel novo.
 *
 * Recusada a alternativa de manter o `switch` e lancar no `default`: ela troca o
 * menu errado por uma tela quebrada em producao e adia a decisao para depois do
 * deploy. `Record<Role, ...>` a antecipa para o `tsc`.
 */
export const NAV_BY_ROLE: Record<Role, readonly NavItem[]> = {
  // O cadastro e exclusivo de Super Admin no contrato; para os outros papeis o
  // item nem aparece, porque menu que leva a uma tela recusada e ruido.
  super_admin: [
    HOME,
    SCHEDULE,
    SCHEDULE_CHANGES,
    SERIES,
    MESSAGES,
    ANNOUNCEMENTS,
    RECORDINGS,
    MAKEUP,
    ATTENDANCE,
    MATERIALS,
    ASSESSMENT_SETTINGS,
    STUDENTS,
    TEACHERS,
    PLANS,
    ENROLLMENTS,
    FINANCE,
    PRICING,
    PAYROLL,
    EXECUTIVE,
    REMINDERS,
  ],
  teacher: [
    HOME,
    SCHEDULE,
    SCHEDULE_CHANGES,
    MESSAGES,
    ANNOUNCEMENTS,
    RECORDINGS,
    MAKEUP,
    ATTENDANCE,
    MATERIALS,
    EARNINGS,
    REMINDERS,
  ],
  student: [
    HOME,
    SCHEDULE,
    SCHEDULE_CHANGES,
    MESSAGES,
    ANNOUNCEMENTS,
    RECORDINGS,
    MAKEUP,
    ATTENDANCE,
    MATERIALS,
    MY_PERFORMANCE,
    MY_ENROLLMENT,
    BILLING,
    REMINDERS,
  ],
  // O suporte nao tem agenda, matricula nem fatura. O que ele alcanca esta
  // escrito, e nao herdado de um ramo de omissao.
  support: [HOME, MESSAGES, ANNOUNCEMENTS, MATERIALS, REMINDERS],
};

export function navItemsFor(role: Role): readonly NavItem[] {
  return NAV_BY_ROLE[role];
}
